import {
  createContext,
  createEffect,
  createMemo,
  createRoot,
  FlowProps,
  getOwner,
  JSX,
  onCleanup,
  Owner,
  runWithOwner,
} from "solid-js";
import c from "class-c";
import {
  loadStripe,
  Stripe as _Stripe,
  StripeElement,
  StripeElements,
  StripeElementType,
  StripePaymentElement,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js";

import createAsync from "@repo/signals/createAsync";
import Log from "@repo/utils/Log";
import UBoolean from "@repo/utils/UBoolean";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import UString from "@repo/utils/UString";
import Platform from "@repo/utils-client/Platform";
import USolid from "@repo/utils-solid/USolid";

import Spinner from "../loaders/Spinner";
import createDomRect from "../signals/createDomRect";
import Css from "@repo/utils-client/Css";

type Stripe = _Stripe;
namespace Stripe {
  export const load = UFunction.memo((publicKey: string) =>
    loadStripe(publicKey)
      .then((client) => {
        if (client == null) throw new Error("Failed to load Stripe.");
        return client;
      })
      .catch((e) => {
        // Invalidate memo cache to attempt next time
        load.invalidate(publicKey);
        throw e;
      }),
  );

  const Context = createContext<{
    key: string;
    load(): Promise<Stripe>;
    theme?: "light" | "dark";
    optionsFor: { payment?: Options<"payment"> };
  }>();

  export declare namespace Provider {
    type Props = {
      key: string;
      optionsFor?: NonNullable<typeof Context.defaultValue>["optionsFor"];
      theme?: "light" | "dark";
      children: JSX.Element;
    };
  }

  export function Provider({
    key,
    optionsFor = {},
    theme,
    children,
  }: D<Provider.Props>) {
    return (
      <Context.Provider
        value={{
          key,
          optionsFor,
          load: () => load(key),
          theme,
        }}
      >
        {children}
      </Context.Provider>
    );
  }

  export declare namespace Provider.Partial {
    type Props = Partial<Provider.Props>;
  }
  Provider.Partial = ({
    children,
    key,
    ...props
  }: D<Provider.Partial.Props>) => {
    const context = useContext();

    return (
      <Context.Provider
        value={{
          ...context,
          ...props,
          ...(key && { load: () => load(key) }),
        }}
      >
        {children}
      </Context.Provider>
    );
  };

  export function useContext() {
    const context = USolid.useContext(Context);

    if (!context)
      throw new Error(
        "Cannot call Stripe.useContext outside of Stripe.Provider.",
      );

    return context;
  }

  type Events = "ready" | "change" | "focus" | "blur" | "escape";
  type EventsFor<Element extends StripeElement> = {
    [Event in Events as `on${UString.Capitalize<Event>}`]?: Element extends {
      on(eventType: Event, handler: (event: infer E) => any): Element;
    }
      ? (event: E) => any
      : never;
  };

  export type Elements = StripeElements;

  export namespace Elements {
    const Context = createContext<
      (() => Elements | null) & { clientSecret(): string | null }
    >();
    export declare namespace Provder {
      type Props = {
        clientSecret: string | null;
        theme?: "light" | "dark";
        children: JSX.Element;
      };
    }
    export function Provider({
      clientSecret,
      theme,
      children,
    }: D<Provder.Props>) {
      const context = Stripe.useContext();

      const owner = getOwner()!;

      const stripeTheme = createMemo(() => {
        return (
          {
            light: "stripe",
            dark: "night",
          } as const
        )[theme || context.theme || "light"];
      });

      const elements = createAsync(() => {
        if (!Platform.isClient) return null;

        return context.load().then(async (client) => {
          const elements = client.elements({
            clientSecret: await USolid.Accessor.matchValue(
              () => clientSecret,
              UBoolean.isTruthy,
            ),
            appearance: {
              theme: stripeTheme(),
              variables: {
                borderRadius: `${Css.computedPxVariable("--default-border-radius")}px`,
                colorBackground: Css.computedVariable("--container"),
              },
            },
          });

          createRoot((dispose) => {
            (owner.cleanups ??= []).push(dispose);

            createEffect(() => {
              elements.update({
                appearance: {
                  theme: stripeTheme(),
                  variables: {
                    borderRadius: `${Css.computedPxVariable(
                      "--default-border-radius",
                    )}px`,
                    colorBackground: Css.computedVariable("--container"),
                  },
                  rules: {
                    ".Label": {
                      fontSize: `${Css.computedPxVariable(
                        "--small-regular-font-size",
                      )}px`,
                      color: Css.computedVariable("--text-moderate-weak"),
                      marginLeft: `${Css.computedPxVariable(
                        "--base-spacing-unit",
                      )}px`,
                    },
                  },
                },
              });
            });
          });
          return elements;
        });
      }, null);

      return (
        <Context.Provider
          value={Object.assign(elements, { clientSecret: () => clientSecret })}
        >
          {children}
        </Context.Provider>
      );
    }
    export function useContext() {
      const context = USolid.useContext(Context);

      if (!context)
        throw new Error(
          "Cannot call Stripe.Elements.useContext outside of Stripe.Elements.Provider.",
        );

      return context;
    }
  }

  type Options<Type extends StripeElementType> =
    Extract<
      StripeElement,
      {
        on(
          eventType: string,
          handler: (event: { elementType: Type }) => any,
        ): any;
      }
    > extends { update(options: infer Options): any }
      ? Options
      : never;

  const mountStripeElement =
    <Type extends StripeElementType>(
      type: Type,
      options: Options<Type>,
      onEvent: (
        callbackName:
          | "onChange"
          | "onReady"
          | "onFocus"
          | "onBlur"
          | "onEscape",
        e: any,
      ) => void,
    ) =>
    (ref: HTMLDivElement) => {
      const stripe = Stripe.useContext();
      const elements = USolid.Accessor.matchValue(
        Elements.useContext(),
        UBoolean.isTruthy,
      );

      onCleanup(async () => {
        (await elements).getElement(type as any)!.destroy();
      });

      return elements.then((elements) => {
        const element = elements.create(
          type as any,
          UProxy.merge((stripe.optionsFor as any)[type] || {}, options as any),
        );

        element.on("change", (e) => onEvent?.("onChange", e));
        element.on("ready", (e) => onEvent?.("onReady", e));
        element.on("focus", (e) => onEvent?.("onFocus", e));
        element.on("blur", (e) => onEvent?.("onBlur", e));
        element.on("escape", (e) => onEvent?.("onEscape", e));

        element.mount(ref);

        return element;
      });
    };

  export declare namespace PaymentElement {
    type Props = UObject.Override<
      EventsFor<StripePaymentElement>,
      {
        class?: string;
        onChange(event: StripePaymentElementChangeEvent): void;
        autofocus?: boolean;
      }
    >;
  }

  export function PaymentElement(props: D<PaymentElement.Props>) {
    const rect = createDomRect({ only: ["size"] });

    return (
      <div
        class={c`transition-normal ${props.class}`}
        style={{ height: `${Math.max(rect()?.height || 0, 24)}px` }}
      >
        {(rect()?.height || 0) < 8 && <Spinner.Common />}
        <div
          ref={(ref) => {
            mountStripeElement("payment", {}, (callbackName, e) =>
              props[callbackName]?.(e),
            )(ref).then((element) => {
              element.on("ready", () => {
                if (props.autofocus) element.focus();
              });
            });
            rect.ref = ref;
          }}
        />
      </div>
    );
  }
  PaymentElement.confirmSetup = async (owner: Owner) =>
    runWithOwner(owner, async () => {
      const stripe = useContext();
      const elements = Elements.useContext();

      if (!elements())
        throw new Error(
          "Elements must be loaded before calling Stripe.PaymentElement.confirmSetup.",
        );

      elements()!.submit();
      return (await stripe.load()).confirmSetup({
        elements: elements()!,
        clientSecret: elements.clientSecret()!,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {
              address: {
                country: "US",
              },
            },
          },
        },
      });
    })!;
  PaymentElement.confirmPayment = async (owner: Owner) =>
    runWithOwner(owner, async () => {
      const stripe = useContext();
      const elements = Elements.useContext();

      if (!elements())
        throw new Error(
          "Elements must be loaded before calling Stripe.PaymentElement.confirmPayment.",
        );

      elements()!.submit();
      return (await stripe.load()).confirmPayment({
        elements: elements()!,
        clientSecret: elements.clientSecret()!,
        redirect: "if_required",
      });
    })!;
}

export default Stripe;
